import { Clock, Moon, NotebookPen, Sparkles } from "lucide-react";
import type { FocusSession } from "../types";

type Props = {
  sessions: FocusSession[];
  loading?: boolean;
  error?: string;
};

function formatDuration(elapsedSeconds: number) {
  const total = Math.max(0, Math.floor(elapsedSeconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
}

function formatWhen(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function totalMinutes(sessions: FocusSession[]) {
  const seconds = sessions.reduce((sum, session) => sum + (session.elapsedSeconds || 0), 0);
  return Math.round(seconds / 60);
}

export function FocusHistoryPanel({ sessions, loading, error }: Props) {
  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="inline-flex items-center gap-2 text-lg font-bold">
                <Clock size={18} aria-hidden="true" /> Lịch sử phiên tập trung
              </h1>
              <p className="text-sm font-medium text-muted">
                Các phiên học đã ghi lại, kèm thẻ Mơ khi bạn quay lại ôn.
              </p>
            </div>
            <div className="flex gap-4 text-right">
              <div>
                <p className="text-xl font-bold text-brand">{sessions.length}</p>
                <p className="text-[11px] font-medium text-muted">phiên</p>
              </div>
              <div>
                <p className="text-xl font-bold text-brand">{totalMinutes(sessions)}</p>
                <p className="text-[11px] font-medium text-muted">phút tập trung</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4">
          {loading ? <p className="text-sm text-muted">Đang tải lịch sử...</p> : null}
          {error ? <p className="text-sm font-medium text-rose-600">{error}</p> : null}
          {!loading && !error && sessions.length === 0 ? (
            <div className="rounded-lg border border-dashed border-line px-4 py-10 text-center">
              <Moon size={28} className="mx-auto text-slate-300" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-slate-600">Chưa có phiên nào.</p>
              <p className="text-xs text-muted">
                Bắt đầu một "Phiên tập trung" ở cột trái, bấm Dừng để ghi lại phiên đầu tiên.
              </p>
            </div>
          ) : null}

          <ul className="space-y-3">
            {sessions.map((session) => (
              <li key={session.id} className="rounded-lg border border-line bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink">
                    <Clock size={14} aria-hidden="true" /> {formatWhen(session.startedAt)}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-muted">
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
                      {formatDuration(session.elapsedSeconds)}
                    </span>
                    <span>mục tiêu {session.workMinutes}p</span>
                    {typeof session.noteCount === "number" ? (
                      <span className="inline-flex items-center gap-1">
                        <NotebookPen size={12} aria-hidden="true" /> {session.noteCount}
                      </span>
                    ) : null}
                    {session.mode === "due" ? (
                      <span className="rounded bg-indigo-100 px-1.5 py-0.5 font-semibold text-indigo-700">ôn lại</span>
                    ) : null}
                  </span>
                </div>

                {session.context ? (
                  <p className="mt-1.5 text-xs italic text-slate-500">“{session.context}”</p>
                ) : null}

                {session.recall || session.reflect || session.encourage ? (
                  <div className="mt-2 space-y-1.5 rounded-lg bg-slate-50 px-3 py-2">
                    {session.recall ? (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-muted">Nhớ lại</p>
                        <p className="whitespace-pre-line text-sm leading-6 text-ink">{session.recall}</p>
                      </div>
                    ) : null}
                    {session.reflect ? (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-muted">Ngẫm</p>
                        <p className="text-sm leading-6 text-slate-700">{session.reflect}</p>
                      </div>
                    ) : null}
                    {session.encourage ? (
                      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700">
                        <Sparkles size={13} aria-hidden="true" /> {session.encourage}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-1.5 text-xs text-slate-400">— chưa xem thẻ Mơ cho phiên này —</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
